import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import LockOutlineRoundedIcon from "@mui/icons-material/LockOutlineRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { AuthCardTopBar, AuthCardIntro } from "./AuthCardHeader";
import { useChangePasswordTemporary } from "../mutations";
import { PasswordStrengthIndicator } from "./PasswordStrengthIndicator";
import {
  getPasswordRequirements,
  isPasswordValid,
} from "../utils/passwordValidation";

interface TemporaryPasswordSetupProps {
  username: string;
  temporaryPassword: string;
  onSuccess: (newPassword: string) => void;
  onError: (error: string) => void;
}

export const TemporaryPasswordSetup: React.FC<TemporaryPasswordSetupProps> = ({
  username,
  temporaryPassword,
  onSuccess,
  onError,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState(false);

  const changePasswordMutation = useChangePasswordTemporary();

  const passwordRequirements = useMemo(() => {
    return getPasswordRequirements(password, confirmPassword);
  }, [password, confirmPassword]);

  const isPasswordValidCheck = isPasswordValid(passwordRequirements);
  const showPasswordError = touched && !isPasswordValidCheck;
  const showConfirmPasswordError =
    touched &&
    !passwordRequirements.find((req) => req.key === "passwordsMatch")?.isValid;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (!isPasswordValidCheck) {
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        username,
        temporaryPassword,
        newPassword: password,
      });
      onSuccess(password);
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Failed to update your password"
      );
    }
  };

  return (
    <Card
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: "divider",
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 4,
        }}
      >
        <Stack spacing={4}>
          <AuthCardTopBar />
          <AuthCardIntro
            title="Update your temporary password"
            subtitle="Create a new password to finish setting up your account."
          />

          <Stack spacing={2}>
            <TextField
              fullWidth
              label="New password"
              value={password}
              type={showPassword ? "text" : "password"}
              onChange={(event) => setPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showPasswordError}
              placeholder="Enter your new password"
              helperText={
                showPasswordError
                  ? "Password must meet all requirements below."
                  : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm password"
              value={confirmPassword}
              type={showConfirmPassword ? "text" : "password"}
              onChange={(event) => setConfirmPassword(event.target.value)}
              onBlur={() => setTouched(true)}
              error={showConfirmPasswordError}
              placeholder="Confirm your new password"
              helperText={
                showConfirmPasswordError ? "Passwords do not match." : ""
              }
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlineRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {password && (
            <PasswordStrengthIndicator
              requirements={passwordRequirements}
              showProgress={true}
            />
          )}

          <Button
            type="submit"
            size="large"
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            disabled={changePasswordMutation.isPending}
            sx={{ mt: 4, mb: 2 }}
          >
            {changePasswordMutation.isPending
              ? "Saving..."
              : "Update password"}
          </Button>
        </Stack>
      </Box>
    </Card>
  );
};
